import {Money} from '@sollapay/utils';

import type {TrustAccountLedgerDTO, TrustAccountPayoutBucketDTO} from '@sollapay/types';

/** `count` is null when the in-flight search failed: the amount is known, the count isn't. */
export interface BreakdownBucket {
  count: number | null;
  amount: Money;
}

export interface BalanceBreakdown {
  confirmed: Money;
  incoming: BreakdownBucket & {expectedBy: string | null};
  outgoing: {
    total: BreakdownBucket;
    sentToBank: BreakdownBucket & {sentAt: string | null};
    approved: BreakdownBucket;
    awaitingReview: BreakdownBucket;
  };
  availableNow: Money;
  expected: Money;
  hasInflight: boolean;
}

/**
 * Stitches the ledger balance, the in-flight summary and reserved payouts into one shape. Outgoing
 * is everything holding back balance: sent to the bank, approved, or awaiting ops review.
 */
export function computeBalanceBreakdown(balance: TrustAccountLedgerDTO): BalanceBreakdown {
  const {deposits, payouts} = balance.inflightSummary ?? {};
  const confirmed = Money.from(balance.balance);
  const incoming = {
    count: deposits?.count ?? null,
    amount: Money.from(balance.inflightCreditBalance),
    expectedBy: deposits?.expectedBy ?? null
  };
  const sentToBank = {
    count: payouts?.count ?? null,
    amount: Money.from(balance.inflightDebitBalance),
    sentAt: payouts?.sentAt ?? null
  };
  const approved = toBucket(balance.reservedPayouts?.approved);
  const awaitingReview = toBucket(balance.reservedPayouts?.awaitingReview);
  const total = {
    count: sumCounts([sentToBank, approved, awaitingReview]),
    amount: Money.sum([sentToBank.amount, approved.amount, awaitingReview.amount])
  };

  return {
    confirmed,
    incoming,
    outgoing: {total, sentToBank, approved, awaitingReview},
    availableNow: Money.from(balance.availableForPayout ?? '0'),
    expected: confirmed.add(incoming.amount).subtract(total.amount),
    hasInflight: !incoming.amount.isZero() || !total.amount.isZero()
  };
}

function toBucket(bucket: TrustAccountPayoutBucketDTO | undefined): BreakdownBucket {
  return {count: bucket?.count ?? 0, amount: Money.from(bucket?.amount ?? '0')};
}

/** Unknown only if a bucket that actually holds money has an unknown count. */
function sumCounts(buckets: BreakdownBucket[]): number | null {
  if (buckets.some(({count, amount}) => count === null && !amount.isZero())) return null;
  return buckets.reduce((total, {count}) => total + (count ?? 0), 0);
}
